import { useState, useEffect, useMemo } from "react";
import { ReactComponent as SpinnerIcon } from "../../../assets/img/icons/spinner.svg";
import { useClassApi, useStateApi, useSchoolApi, useCountryApi } from "../../../Hooks";
import { Table } from "../../Shared/Table";
import { Pagination } from "./../../Shared/Pagination";
import SearchBar from './../../Shared/SearchBar';
import Dropdown from './../../Shared/Dropdown';
import { truncate } from "../../../Utils/TextUtils";
import DeleteItemButton from './../../Shared/Buttons/DeleteItemButton';
import EditItemButton from './../../Shared/Buttons/EditItemButton';
import CreateButton from './../../Shared/Buttons/CreateButton';
import ConfirmModal from "../../Shared/ConfirmModal";
import { useNavigate } from 'react-router-dom';
import {useTranslation} from "react-i18next";
import {getLocalizedRoutePath, ROUTE_KEYS} from "../../../routes/definitions";
export default function Turmas() {
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [listData, setListData] = useState({
    page: 1,
    pageCount: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const classApi = useClassApi();  
  const stateApi = useStateApi(); 
  const schoolApi = useSchoolApi();
  const countryApi = useCountryApi(); 
  const [search, setSearch] = useState("");

  const [countries, setCountries] = useState([]);
  const [countryFilter, setCountryFilter] = useState(null);

  const [states, setStates] = useState([]);
  const [stateFilter, setStateFilter] = useState(null);

  const [cities, setCities] = useState([]);
  const [cityFilter, setCityFilter] = useState(null);

  const [schools, setSchools] = useState([]);
  const [schoolFilter, setSchoolFilter] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalLoading, setConfirmModalLoading] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({message: "", itemId: ""});
  const [confirmModalAlert, setConfirmModalAlert] = useState({});
  const [confirmModalButtonsEnabled, setConfirmModalButtonsEnabled] = useState(true);

  const navigate = useNavigate();
  const {t, i18n} = useTranslation();
  const language = i18n.language;


  useEffect(() => {
    setLoading(true);
    const loadData = async () => {
      await new Promise((r) => setTimeout(r, 500));
      const getClasses = await classApi.classList(
        listData.page,
        listData.itemsPerPage,
        "",
        "",
        ""
      );
      setClasses(getClasses.items);
      setListData({
        ...listData,
        pageCount: getClasses.pageCount,
        totalItems: getClasses.totalItems,
      });
      loadCountries();
      setLoading(false);
    };

    loadData();
  }, []);

  const loadCountries = async () => {
    const getCountries = await countryApi.countryList();
    const countries = getCountries.map((country) => ({
      value: country.id,
      label: country.abbreviation,
    }));
    setCountries(countries);
  };  

  const loadStates = async (country) => {
    if (country === null) {
      setStates([]);
      setCities([]);
      setStateFilter(null);
      setCityFilter(null);
      return;
    }

    const getStates = await stateApi.stateList(country.value);
    const states = getStates.map((state) => ({
      value: state.id,
      label: state.name,
    }));
    setStates(states);
  }

  const loadCities = async (state) => {
    if(state === null){
      setCities([]);
      setCityFilter(null);
      return;
    }

    const getCities = await stateApi.cityList(state.value);
    const cities = getCities.map((city) => ({
      value: city.id,
      label: city.name,
    }));
    setCities(cities);
  }

  const loadSchools = async (city) => {
    if(city === null){
      setSchools([]);
      setSchoolFilter(null);
      return;
    }

    const getSchools = await schoolApi.schoolList(0,0,"", city.value, stateFilter.value );
    const schools = getSchools.items.map((school) => ({
      value: school.id,
      label: school.name,
    }));
    setSchools(schools);
  }

  const reloadData = async (page, cityFilter = "", stateFilter="", schoolFilter="", countryFilter = "") => {
    setLoadingData(true);
    await new Promise((r) => setTimeout(r, 500));
    var cityFilterValue = cityFilter !== null ? cityFilter.value : "";
    var stateFilterValue = stateFilter !== null ? stateFilter.value : "";
    var schoolFilterValue = schoolFilter !== null ? schoolFilter.value : "";
    var countryFilterValue = countryFilter !== null ? countryFilter.value : "";

    const getClasses = await classApi.classList(
      page,
      listData.itemsPerPage,
      search,
      cityFilterValue,
      stateFilterValue,
      schoolFilterValue,
      countryFilterValue
    );

    setClasses(getClasses.items);
    setListData({
      ...listData,
      page: page,
      pageCount: getClasses.pageCount,
      totalItems: getClasses.totalItems,
    });
    setLoadingData(false);
    
  };

  const handleSearchClick = async (search) => {    
    reloadData(1, cityFilter, stateFilter, schoolFilter);
  }

  const handleCountryFilterChange = async (e) => {
    setCountryFilter(e);
    setStateFilter(null);
    setCityFilter(null);
    setSchoolFilter(null);
    setStates([]);
    setCities([]);
    setSchools([]);
    setClasses([]);
    
    if (e != null) {
      await loadStates(e);
    }

    reloadData(1, null, null, null, e);
  }    
  
  const handleStateFilterChange = async (e) => {
    setStateFilter(e);
    if (e === null) {
      setCityFilter(null);
      setSchoolFilter(null); // Limpar filtro de escola ao limpar o filtro de estado
      setCities([]); // Limpar filtro de cidade ao limpar o filtro de estado
      setSchools([]); // Limpar dados de escola ao limpar o filtro de estado
      setClasses([]); // Limpar filtro de turma ao limpar o filtro de estado
    } else {
      setCityFilter(null);
      setSchoolFilter(null);
      await loadCities(e);
    }
    reloadData(1, null, e, null);
  }
  

  const handleCityFilterChange = async (e) => {
    setCityFilter(e);
    setSchoolFilter(null);
    loadSchools(e);    
    reloadData(1, e, stateFilter, null);
  }

  
  const handleSchoolFilterChange = async (e) => {
    setSchoolFilter(e);
    reloadData(1, cityFilter, stateFilter, e);
  }

  const handlePageClick = (event) => {
    reloadData(event.selected + 1,cityFilter,stateFilter);
  };

  const handleDeleteClick = async (name, id) => {
    setConfirmModalAlert({});     
    setConfirmModalButtonsEnabled(true);
    setShowConfirmModal(true);
    setConfirmModalData({
      message: t("registrationsPage.confirmations.deleteClass", { name }),
      itemId: id,
    });    
  }  

  const handleEditClick = async (id) => {
    navigate(getLocalizedRoutePath(ROUTE_KEYS.REGISTRATIONS_CLASS_EDIT, language, { id: String(id) }));
  }  
  
  const handleCreateClick = async () => {
    navigate(getLocalizedRoutePath(ROUTE_KEYS.REGISTRATIONS_CLASS_CREATE, language));
  }  

  const handleDeleteConfirm = async () => {
    setConfirmModalLoading(true);    
    setConfirmModalButtonsEnabled(false);
    await new Promise((r) => setTimeout(r, 500));
    const deleteClass = await classApi.deleteClass(confirmModalData.itemId);
    if(deleteClass.status === 204){
      setConfirmModalLoading(false);
      setConfirmModalAlert({
        message: t("registrationsPage.success.classDeleted"),
        type: "success"
      });
      await new Promise((r) => setTimeout(r, 1000));
      reloadData(listData.page);            
      setShowConfirmModal(false);      
    }
    else {
      setConfirmModalAlert({message: deleteClass.response.data[0], type: "error"});
      setConfirmModalLoading(false);
      setConfirmModalButtonsEnabled(true);
    }      
  }  

  const columns = useMemo(
    () => [
      {
        Header: t("registrationsPage.tables.classes.columns.id"),
        accessor: "id",
        show: false
      },
      {
        Header: t("registrationsPage.tables.classes.columns.name"),
        accessor: "name",
        Cell: ({ value }) =><p data-tip={value} className="font-dosisbold text-playmove-darkgray">{truncate(value, 30)}</p>,
        width: "20%"        
      },
      {
        Header: t("registrationsPage.tables.classes.columns.school"),
        accessor: "schoolName",
        Cell: ({ value }) =><p data-tip={value}>{truncate(value, 30)}</p>,
        width: "30%"        
      },
      {
        Header: t("registrationsPage.tables.classes.columns.country"),
        accessor: "countryAbbreviation",
        width: "10%"
      },      
      {
        Header: t("registrationsPage.tables.classes.columns.state"),
        accessor: "stateAbbreviation",
        width: "10%"
      },
      {
        Header: "CityId",
        accessor: "cityId",
        show: false
      },
      {
        Header: t("registrationsPage.tables.classes.columns.city"),
        accessor: "cityName",
        width:"20%"
      },
      {
        Header: "StateId",
        accessor: "stateId",
        show: false
      },
      {
        Header: "CountryId",
        accessor: "countryId",
        show: false
      },
      {
        Header: "CourseCount",
        accessor: "courseCount",
        show: false
      },
      {
        Header: t("registrationsPage.tables.classes.columns.studentsCourses"),
        accessor: "studentCount",
        Cell: ({ row }) =>
          <div className="flex flex-col text-base text-center">
            <p>{row.values.studentCount > 0 ? t("registrationsPage.tables.classes.studentsCount", { count: row.values.studentCount }) : "-"}</p>
            <p>{row.values.courseCount > 0 ? t("registrationsPage.tables.classes.coursesCount", { count: row.values.courseCount }) : "-"}</p>
          </div>,
        width: "10%"
      },
      {
        Header: " ",
        accessor: "",
        Cell: ({ row }) => (
          <div className="ml-auto w-56 flex flex-row gap-x-20">
            <DeleteItemButton onClick={() => handleDeleteClick(row.values.name, row.values.id)} />
            <EditItemButton onClick={() => handleEditClick(row.values.id)}/>
          </div>
        ),
      width:"10%"
      },
    ],

    [t]
  );

  return (
    <div className="flex flex-col">
      {loading ? (
        <SpinnerIcon className="m-auto" />
      ) : (
        <div className="-ml-12 w-screen md:w-full md:ml-0 flex flex-col">
          <div className="flex flex-col flex-wrap items-center md:flex-row my-3 gap-y-3 gap-x-3">
          <SearchBar value={search} onClick={handleSearchClick} onChangeText={(e) => setSearch(e.target.value)} placeholder={t("registrationsPage.filters.searchClasses")} />
          <Dropdown items={countries} value={countryFilter} placeholder={t("registrationsPage.filters.byCountry")} onChange={handleCountryFilterChange}/>
          <Dropdown items={states} value={stateFilter} placeholder={t("registrationsPage.filters.byState")} onChange={handleStateFilterChange} emptyMessage={t("registrationsPage.placeholders.selectCountry")} />
          <Dropdown items={cities} value={cityFilter} placeholder={t("registrationsPage.filters.byCity")} emptyMessage={t("registrationsPage.placeholders.selectState")} onChange={handleCityFilterChange}/>
          <Dropdown items={schools} value={schoolFilter} placeholder={t("registrationsPage.filters.bySchool")} emptyMessage={t("registrationsPage.placeholders.selectCityWithSchools")} onChange={handleSchoolFilterChange}/>
          <CreateButton onClick={handleCreateClick} className="lg:ml-auto" text={t("registrationsPage.actions.addClass")}/>
          </div>         
          <ConfirmModal isOpen={showConfirmModal} alert={confirmModalAlert} loading={confirmModalLoading} onConfirm={handleDeleteConfirm} buttonsEnabled={confirmModalButtonsEnabled} onClose={() => setShowConfirmModal(false)} message={confirmModalData.message}/>                 
          <Table className="w-full" columns={columns} data={classes} loadingContent={loadingData} />          
          <Pagination
            className="ml-auto mt-3"
            pageCount={listData.pageCount}
            currentPage={listData.page - 1}
            handlePageClick={handlePageClick}
          />
        </div>
      )}
    </div>
  );
}
